import React, { useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import Heading from '@atlaskit/heading/heading';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';
import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	useAnalytics,
	usePrincipalUser,
	useTeamCentralStore,
} from '@atlassian/ptc-common';
import {
	SURFACE_ANALYTICS_PRODUCT_MAP,
	useFireCrossFlowAnalyticsEvents,
} from '@atlassian/surface-analytics';

import { useReportingLinesContext } from '../../ReportingLinesContext';

import MeepleOrgChart from './MeepleOrgChart_ptc.svg';
import { messages } from './messages';
import * as Styled from './styles';

const ATLAS_CCP_ID = SURFACE_ANALYTICS_PRODUCT_MAP.products.townsquare;

const MeepleOrgChartSvg = () => {
	return <MeepleOrgChart />;
};

const getInfoMessage = (isAdmin: boolean) =>
	isAdmin ? messages.AdminEmptyStateMessage : messages.UserEmptyStateMessage;

export const ReportingLinesEmptyState = (): React.JSX.Element => {
	const [{ isSiteAdmin }] = usePrincipalUser();
	const { fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();

	const analytics = useAnalytics();
	const { fireEvent } = useAnalyticsEvents();

	const [teamCentralStore] = useTeamCentralStore();
	const { getActiveWorkspaceId } = teamCentralStore.teamCentralHandlers;
	const { product = '' } = useReportingLinesContext();
	const workspaceUuid = getActiveWorkspaceId ? getActiveWorkspaceId() : null;

	useEffect(() => {
		if (fg('ptc-enable-ptc-common-analytics-refactor')) {
			fireEvent('ui.ReportingLinesEmptyState.viewed', {
				isAdmin: isSiteAdmin,
				product,
				workspaceUuid,
			});
		} else {
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'ReportingLinesEmptyState',
				action: AnalyticsEventActions.VIEWED,
				tags: ['watermelon'],
				attributes: {
					isAdmin: isSiteAdmin,
					product,
					workspaceUuid,
				},
			});
		}

		// UCIA viewed event
		fireViewedUIEvent(
			{
				action: 'viewed',
				actionSubject: 'section',
				actionSubjectId: 'reportingLinesEmptyState',
			},
			{
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				recommendedProductIds: [ATLAS_CCP_ID],
			},
		);
	}, [analytics, isSiteAdmin, product, workspaceUuid, fireViewedUIEvent, fireEvent]);

	return (
		<Styled.ReportingLinesEmptyStateContainer testId="reporting-lines-empty-state">
			<MeepleOrgChartSvg />
			<Styled.ContentSection>
				<Heading size="small">
					<FormattedMessage {...messages.EmptyStateHeading} />
				</Heading>
				<Styled.InfoMessage>
					<Text as="p">
						<FormattedMessage {...getInfoMessage(isSiteAdmin)} />
					</Text>
				</Styled.InfoMessage>
			</Styled.ContentSection>
		</Styled.ReportingLinesEmptyStateContainer>
	);
};
